/**
 * THE LYRIA 3 CONTRACT — what Google's music model actually accepts and returns.
 *
 * ============================================================================
 * NOTHING HERE ISSUES A REQUEST. This module builds the documented Lyria 3
 * request body and reads the documented Lyria 3 response, and both halves are
 * real, exported and unit tested — but the dispatch is not here and cannot
 * happen from this app today. `describeLyriaBlockers()` below says why, in one
 * place, so the reason is a value the UI can render rather than a comment.
 * ============================================================================
 *
 * WHY A SEPARATE MODULE AND NOT A SECOND API
 * ------------------------------------------
 * It is not an API at all — it is the wire format of the one generation backend the
 * product is built on, factored out of generationApi so the *shape* of a multimodal
 * request can be built and asserted without a server. generationApi is still the only
 * boundary a component talks to (see its header): Image Music calls
 * `generationApi.createGeneration` with images attached, exactly as the composer calls
 * it without them, and this module is what that function would hand to Google.
 *
 * VERIFICATION — read this before changing a model name
 * ----------------------------------------------------
 * Checked against the official page, <https://ai.google.dev/gemini-api/docs/music-
 * generation>, which is the source of truth the product brief names. What it says
 * today, and every claim below is from that page rather than from memory:
 *
 *   - THESE MODELS ARE AVAILABLE, with these exact identifiers:
 *       `lyria-3-clip-preview` — always a 30-second clip. MP3.
 *       `lyria-3-pro-preview`  — full songs of a couple of minutes, with verses,
 *                                choruses and bridges. MP3, or WAV via
 *                                `response_format: { type: 'audio' }`.
 *       `lyria-3.5`              — Google's newer flagship full-song model.
 *     They produce 44.1 kHz stereo and accept multimodal input.
 *
 *   - IMAGE INPUT IS REAL AND DOCUMENTED. Up to ten images alongside the text prompt.
 *     The page states the limit generally rather than per model, so it is enforced
 *     here as a property of the request and not of the model — see MAX_LYRIA_IMAGES.
 *
 *   - ENDPOINT: `POST https://generativelanguage.googleapis.com/v1beta/interactions`
 *     (`interactions.create` in the SDKs), authenticated with an `x-goog-api-key`
 *     header.
 *
 *   - AUDIO IS AN OUTPUT ONLY. There is no parameter that accepts an existing clip,
 *     which is the finding api/audioEditApi.ts is built on and is unchanged by image
 *     input: an image is not audio.
 *
 *   - Generation is single-turn, results are non-deterministic between calls, all
 *     output carries a SynthID watermark, and prompts pass a safety filter that
 *     blocks requests for specific artists' voices or copyrighted lyrics.
 *
 *   - NO seed, temperature, negative-prompt or numeric duration parameter is
 *     documented. Length on the Pro model is influenced by the prompt text. That is
 *     why `buildLyriaRequest` takes no such options: a field the API does not read
 *     would look like a control.
 *
 * WHAT THIS MEANS FOR THE APP TODAY
 * ---------------------------------
 * The request can be built, and is. It cannot be sent, for two independent reasons
 * that both have to be fixed and only one of which is configuration —
 * `describeLyriaBlockers()` returns both. The second is the important one: an API key
 * in a Vite frontend is in the bundle, which means it is in every user's browser, so
 * calling Google from the client is not a shortcut to be taken later. The call belongs
 * to the backend, and this module is the format the backend has to speak.
 */
import { API_BASE_URL } from '../config';
import {
  MUSIC_GENERATION_MODELS,
  STANDARD_MUSIC_MODEL,
  type MusicGenerationModel,
} from '../lib/musicGenerationModels';

/**
 * The supported model identifiers, verbatim from the documentation.
 *
 * A const tuple rather than a string union alias so the set can be iterated and
 * asserted — a test can check that nothing in the app names a model outside it, which
 * is the guard against a plausible-looking "lyria-3.5-pro" appearing in a prompt
 * string six months from now.
 */
export const LYRIA_MODELS = ['lyria-3-clip-preview', ...MUSIC_GENERATION_MODELS] as const;

export type LyriaModel = (typeof LYRIA_MODELS)[number];

/**
 * The model Image Music targets.
 *
 * Pro rather than Clip, because Clip's output is always a fixed 30 seconds and the
 * product asks for a song. Both accept images, so this is a length decision and not a
 * capability one.
 */
export const IMAGE_MUSIC_MODEL: MusicGenerationModel = STANDARD_MUSIC_MODEL;

/**
 * Most images one request may carry.
 *
 * Ten, which is the documented ceiling. The app's own UI collects one at a time today,
 * and that is a UI decision rather than a limit of this boundary — `buildLyriaRequest`
 * accepts an array and blocks nothing below ten, which is what the brief asks for so
 * the documented multimodal capability is not walled off by our own plumbing.
 */
export const MAX_LYRIA_IMAGES = 10;

/**
 * One image, ready for the wire.
 *
 * `data` is BARE base64 — no `data:image/png;base64,` prefix. The documented field
 * takes the encoded bytes on their own, and the browser's FileReader produces a data
 * URL, so the two are bridged by `base64FromDataUrl` below rather than by hoping a
 * caller remembered to strip it.
 */
export interface LyriaImage {
  /** Base64-encoded bytes, without a data-URL prefix. */
  data: string;
  /** e.g. `image/png`. The documented field name on the wire is `mime_type`. */
  mimeType: string;
  /**
   * The file it came from. NOT sent — Lyria has no field for it. Carried so the UI and
   * the song record can say which image a song came from.
   */
  filename?: string;
}

/** A text block in a Lyria `input` array. */
export interface LyriaTextBlock {
  type: 'text';
  text: string;
}

/**
 * An image block in a Lyria `input` array.
 *
 * `mime_type` is snake_case deliberately: this interface describes the JSON body, not
 * our own domain, and renaming it to camelCase here would mean a mapping step whose
 * only purpose is to undo itself.
 */
export interface LyriaImageBlock {
  type: 'image';
  mime_type: string;
  data: string;
}

export type LyriaInputBlock = LyriaTextBlock | LyriaImageBlock;

/**
 * The request body, as documented.
 *
 * `input` is the array form. The docs also accept a bare string for a text-only
 * request; the array is used unconditionally because it is the form that generalises,
 * and one shape is easier to assert than two.
 */
export interface LyriaRequest {
  model: LyriaModel;
  input: LyriaInputBlock[];
}

/** Thrown when a caller asks for a request Lyria could not accept. */
export class LyriaRequestError extends Error {
  readonly name = 'LyriaRequestError';
}

/**
 * Strips a data URL down to the base64 the API wants.
 *
 * `readImageAsDataUrl` (lib/imageUpload) returns `data:image/png;base64,iVBOR…`
 * because a data URL is what an `<img src>` needs for the preview and what survives a
 * reload. Lyria wants the payload alone, with the MIME type in its own field. Passing
 * the whole URL through would be a base64 string that decodes to the ASCII of a data
 * URL, which is the sort of mistake that produces a 400 with no useful message.
 *
 * Returns the input unchanged when it is already bare base64, so a caller holding
 * bytes from somewhere other than a FileReader does not have to know which it has.
 */
export function base64FromDataUrl(value: string): string {
  const comma = value.indexOf(',');
  if (value.startsWith('data:') && comma !== -1) return value.slice(comma + 1);
  return value;
}

/**
 * Reads the MIME type out of a data URL, or null when there is not one to read.
 *
 * A courtesy for callers that have a data URL and no `File` beside it. The upload path
 * uses `File.type`, which is what the browser sniffed and therefore the better source.
 */
export function mimeTypeFromDataUrl(value: string): string | null {
  const match = /^data:([^;,]+)[;,]/.exec(value);
  return match ? match[1] : null;
}

/**
 * Builds the documented Lyria 3 request for a prompt and zero or more images.
 *
 * TEXT FIRST, THEN THE IMAGES, which is the order the documentation's own example
 * uses. It is not arbitrary: the instruction is what tells the model what to do with
 * the pictures, and a request that opens with an image and explains itself afterwards
 * is a different prompt.
 *
 * THE TEXT BLOCK IS STILL REQUIRED BY THIS APP'S REQUEST SHAPE, but it no longer has to
 * come from free-typed user prose. Image Music can build that block from the attached
 * image plus the chosen genre/vocal/language settings, so the request builder only needs
 * some non-empty instruction text by the time it is called.
 *
 * @throws LyriaRequestError on an empty prompt or more than MAX_LYRIA_IMAGES images.
 *   Both are the API's own constraints rather than ours, so they are refused here
 *   instead of being sent and rejected — the message a caller can act on is the one
 *   written by whoever knew the rule.
 */
export function buildLyriaRequest(input: {
  model: LyriaModel;
  prompt: string;
  images?: readonly LyriaImage[];
}): LyriaRequest {
  const prompt = input.prompt.trim();
  if (!prompt) {
    throw new LyriaRequestError('A Lyria request needs some instruction text for the music.');
  }

  const images = input.images ?? [];
  if (images.length > MAX_LYRIA_IMAGES) {
    throw new LyriaRequestError(
      `Lyria 3 accepts up to ${MAX_LYRIA_IMAGES} images per request; ${images.length} were supplied.`
    );
  }

  return {
    model: input.model,
    input: [
      { type: 'text', text: prompt },
      ...images.map((image): LyriaImageBlock => ({
        type: 'image',
        // Defensive in the one way that is not defensiveness: a caller that hands
        // over a whole data URL gets the right request rather than a silent 400.
        mime_type: image.mimeType,
        data: base64FromDataUrl(image.data),
      })),
    ],
  };
}

/**
 * Composes the instruction sent to Lyria for an image-inspired generation.
 *
 * THE USER'S WORDS CARRY THE MUSICAL DIRECTION. This adds the one thing the model
 * cannot infer — that the attached picture is a *reference for the music* rather than,
 * say, cover art to describe — and then gets out of the way. No genre, mood, tempo or
 * instrumentation is hardcoded: a fixed "cinematic ambient" preamble would mean every
 * image produced the same kind of song regardless of what was asked for, which is the
 * failure the brief names explicitly.
 *
 * The trailing options are appended only when the caller has them, and as plain
 * language rather than as pseudo-fields, because Lyria reads a prompt and not a schema
 * — there is no `genre` or `language` parameter to set (see the header).
 */
export function buildImageMusicPrompt(input: {
  prompt: string;
  imageCount: number;
  genre?: string;
  vocal?: string;
  language?: string;
}): string {
  const parts: string[] = [];

  const subject = input.imageCount > 1 ? 'the attached images' : 'the attached image';
  parts.push(
    `Compose original music inspired by ${subject} — its mood, colours, light and subject matter.`
  );

  const instruction = input.prompt.trim();
  if (instruction) parts.push(instruction);

  const settings = [
    input.genre ? `Genre: ${input.genre}.` : null,
    input.vocal ? `Vocals: ${input.vocal}.` : null,
    input.language ? `Sung in ${input.language}.` : null,
  ].filter((line): line is string => line !== null);
  if (settings.length > 0) parts.push(settings.join(' '));

  return parts.join(' ');
}

// ---------------------------------------------------------------------------
// The response
// ---------------------------------------------------------------------------

/** An audio block in a Lyria response step. `data` is base64-encoded audio. */
export interface LyriaAudioBlock {
  type: 'audio';
  data: string;
  /** Present on some responses; the format the bytes are in. */
  mime_type?: string;
}

export type LyriaOutputBlock = LyriaAudioBlock | LyriaTextBlock;

/**
 * One step of the interaction.
 *
 * The documented iteration walks `steps`, keeps the `model_output` ones and reads
 * their `content` blocks. `type` is left as a plain string because the documentation
 * does not enumerate the step kinds, and a union that guessed them would reject a
 * response the API is entitled to send.
 */
export interface LyriaStep {
  type: string;
  content?: LyriaOutputBlock[];
}

/**
 * The response body.
 *
 * `output_audio` and `output_text` are the documented convenience properties — the
 * last audio block and the model's text (lyrics and structure) respectively. Both are
 * optional here because a response can carry one, both or neither, and code that
 * assumed audio was always present would crash on a safety refusal rather than
 * reporting it.
 */
export interface LyriaResponse {
  steps?: LyriaStep[];
  /** Convenience property: the last audio block. */
  output_audio?: LyriaAudioBlock | null;
  /** Convenience property: the model's text output — lyrics, structure. */
  output_text?: string | null;
}

/**
 * Pulls the generated audio out of a response.
 *
 * Prefers `output_audio` and falls back to walking `steps`, because the convenience
 * property is documented but the raw structure is the thing that is actually on the
 * wire — a response missing the shortcut still has the audio in it, and failing to
 * find a track that arrived would be indistinguishable from a generation that failed.
 *
 * Returns null when there is genuinely no audio, which is a real outcome: a prompt
 * blocked by the safety filter returns without any.
 */
export function readLyriaAudio(response: LyriaResponse): LyriaAudioBlock | null {
  if (response.output_audio) return response.output_audio;
  const blocks = (response.steps ?? [])
    .flatMap((step) => step.content ?? [])
    .filter((block): block is LyriaAudioBlock => block.type === 'audio');
  // The LAST one, matching output_audio's documented meaning rather than picking the
  // first and quietly differing from the shortcut this stands in for.
  return blocks.length > 0 ? blocks[blocks.length - 1] : null;
}

/**
 * Pulls the model's text out of a response — the lyrics, where it produced any.
 *
 * Read, and not discarded, because the app has a field for it: `Song.lyrics`. The
 * brief asks for generated lyrics to be preserved "using the existing song/lyrics
 * data model", and this is the half of that which belongs to the wire format.
 *
 * Joins every text block rather than taking one: a Pro response can describe structure
 * in one block and lyrics in another, and keeping the first only would silently drop
 * the verses.
 */
export function readLyriaText(response: LyriaResponse): string | null {
  if (typeof response.output_text === 'string' && response.output_text.trim()) {
    return response.output_text;
  }
  const text = (response.steps ?? [])
    .flatMap((step) => step.content ?? [])
    .filter((block): block is LyriaTextBlock => block.type === 'text')
    .map((block) => block.text)
    .filter((line) => line.trim())
    .join('\n\n');
  return text ? text : null;
}

// ---------------------------------------------------------------------------
// What stands in the way
// ---------------------------------------------------------------------------

/**
 * One reason a Lyria request cannot be issued from this application.
 *
 * A record rather than prose so the UI can render the list and the §16 report can
 * quote it, and so the two cannot disagree about what is missing.
 */
export interface LyriaBlocker {
  /** Short identifier, for tests and for keys. */
  id: 'no-backend' | 'no-credentials';
  /** One line, in the words the user or the reviewer needs. */
  summary: string;
  /** What would have to change. */
  resolution: string;
}

/**
 * Everything that has to be true before Lyria 3 can actually be called, and is not.
 *
 * Computed rather than constant, so the day `VITE_API_BASE_URL` is set the first entry
 * disappears on its own and the UI's notice shortens without an edit. Returns an empty
 * array only when a backend is configured — at which point `assertMock` in
 * generationApi is what fails, loudly, until the real request is implemented there.
 *
 * THE SECOND BLOCKER IS NOT A TODO. It is the reason the first one is the fix: an API
 * key given to a Vite build is inlined into the bundle and served to every visitor, so
 * "add VITE_GEMINI_API_KEY" is not a smaller version of "add a backend" — it is
 * publishing the key. There is deliberately no client-side key path in src/config.ts
 * for the same reason, and adding one should be treated as a regression.
 */
export function describeLyriaBlockers(): LyriaBlocker[] {
  const blockers: LyriaBlocker[] = [];

  if (!API_BASE_URL) {
    blockers.push({
      id: 'no-backend',
      summary: 'No Lyrixsa backend is configured, so there is nothing to send the request to.',
      resolution:
        'Set VITE_API_BASE_URL and implement POST /generations server-side, forwarding the ' +
        'request this app builds to the Lyria 3 Interactions API.',
    });
  }

  blockers.push({
    id: 'no-credentials',
    summary:
      'Google requires an x-goog-api-key on every Interactions request, and the browser ' +
      'cannot hold one.',
    resolution:
      'Keep the key server-side. A key given to a Vite build is inlined into the bundle and ' +
      'shipped to every visitor, so the call has to be made by the backend rather than by ' +
      'this app.',
  });

  return blockers;
}

/** True when a Lyria request could actually be dispatched. False today. */
export function canRequestLyria(): boolean {
  return describeLyriaBlockers().length === 0;
}
